import configPreferences from "csi/config/preferences";
import ExtendedPath from "csi/core/ExtendedPath";
import PathHints from "csi/core/PathHints";
import def from "csi/def";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import { PreferencePath } from "csi/def/PreferencePath";
import csiHeading from "csi/heading/_base";
import locale from "csi/locale";
import { DEFAULT_IMAGE_LABEL, isImageLabelKey } from "csi/model/image";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { TYPE_USER, getClass, getClassAttribute, isDividerType } from "csi/reflection/reflection";
import state from "csi/state";
import { StoreNodes } from "csi/store/Nodes";
import types from "csi/types";
import { ATTR_NAME, ATTR_TYPE, C8AttributeKey, C8AttributeType, C8NodeType, VALUE_TYPE, isMapValueType } from "csi/typings";
import xml from "csi/xml";
import { compact, each, groupBy, uniqueId } from "lodash";
import { computed, makeObservable } from "mobx";
import Key from "./Key";
import Spec from "./Spec";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { getConcretePathForAttribute } from "./extendedPathService";

// TODO
// * Test PdfGenerate case with ClassSettings vs _CS_MasterSettings
// * Resolve subheadings

// TEST SLICER

// TEST CameraPositionMatrix

// PrintDesign Positions
// DP: Images/:Child:PrintPositionColors/Child:PrintPositionColorSpecification:0{PrintPositionColors} => Images:Child:PrintPositionColors/Child:PrintPositionColorSpecification:0

type SpecNameConditionFunction = (key: Key) => boolean;
type SpecNameFunction = (key: Key) => string;

interface IConfigurableQueryLevel {
	/** store used to query for possible filter values */
	configurableStore: StoreNodes;

	/** unique id for level */
	id: C8AttributeKey;

	/** name of matrix */
	name: string;

	/** column node type */
	type: C8NodeType;
}

const DEFAULT_PATH = "";
const customNameFunctions: [SpecNameConditionFunction, SpecNameFunction][] = [];

/** return true if heading references image map and needs default image index */
function needsDefaultImageIndex(heading: Heading, type: C8AttributeType): boolean {
	const { classRenderer, m_format, m_index } = heading;

	return !classRenderer &&
		(m_format == types.FORMAT_IMAGE) &&
		isNullOrUndefined(m_index) &&
		isMapValueType(type);
}

/** return string with combination of extended path and matrix group id */
function pathPlusMatrixGroupId(extendedPath: ExtendedPath, matrixGroupId?: string): string {
	const pathId = extendedPath?.value ?? DEFAULT_PATH;
	return matrixGroupId ? `${pathId}{${matrixGroupId}}` : pathId;
}

/**
 * Resolve Spec key against model, filling in class and attribute parameters for Spec
 * @param {Object} params - parameters that will be used to eventually create the Spec
 */
function resolveSpecParameters(specBuilder: SpecBuilder, params: Partial<Spec>): void {
	const { defaultPathHints, defaultSourceClass } = specBuilder;
	const { heading, key } = params;
	const { m_id: id, isMatrixGroup, matrix, m_subheadings } = heading;
	const { extendedPathThroughAttribute, isForShowingReferencedImages, path, prop, reflectionAttribute, valueType } = key;
	let { format } = params;
	let { extendedPath } = key;
	const sourceClass = defaultSourceClass || extendedPath?.startClass;

	if (sourceClass && !extendedPath) {
		// create extended path if not provided
		extendedPath = ExtendedPath.create(sourceClass, path, defaultPathHints);
		if (extendedPath) {
			params.key = key.copy({ extendedPath });
		}
	}

	const rc = extendedPath?.getEndClass();

	params.type = valueType;

	// use id from Heading rather than key as it is possible the Heading has a Name and Id, and Name is used resulting key
	if (id) {
		if (reflectionAttribute) {
			params.ra = reflectionAttribute;
			params.rcDest = xml.attributeTypeIsRef(valueType) ? extendedPathThroughAttribute?.endClass : null;
			format ||= isForShowingReferencedImages ? types.FORMAT_REFERENCED_IMAGE : reflectionAttribute.getFormat();
		} else if (id == ATTR_TYPE) {
			// use specific format for type
			format = types.FORMAT_TYPE;
		}
	} else if (prop) {
		if (prop === xml.PROP_CREATOR) {
			params.rcDest = getClass(TYPE_USER);
		}
	} else if (!params.type && !matrix) {
		const ren = csiHeading.getHeadingRenderer(null, heading);
		params.type = ren?.type ?? VALUE_TYPE.STRING;
	}

	params.rc = rc;

	// FIX: the goal is to eventually remove resolving of Headings and instead put (most) everything on Spec (params)
	//	augment heading
	//	heading.m_bRequired
	//	heading.m_link
	//	heading.m_name
	//	heading.m_nameOrig
	//	heading.m_tooltip
	//	heading.m_right
	//	heading.m_action
	//	heading.m_defActions
	heading.resolveHeading(locale.localeResolver, reflectionAttribute, rc, sourceClass, (heading.rc !== rc));

	// copy specific properties to Heading so it has access to them (until we start to work with Spec directly)
	heading.valueType = params.type;

	// override format if Heading format is set explicitly
	format ||= heading.m_format;

	// set format for date attributes without a format
	if (!format && (params.type == xml.ATTR_TIME)) {
		format = types.FORMAT_DATE;
	}

	params.format = heading.m_format = format;

	// show default image unless index already specified, or there is a custom renderer class
	if (needsDefaultImageIndex(heading, params.type)) {
		params.index = DEFAULT_IMAGE_LABEL;
		heading.m_index = DEFAULT_IMAGE_LABEL;
	}

	// ensure this is done after call to resolveHeading - if a matrix such as WBS ElementsByName is defined at Node/Headings level,
	// then matrix will not be known until after resolving
	params.matrixExtendedPath = specBuilder.getMatrixGroupExtendedPath(heading);

	// resolve any subheadings
	if (!isMatrixGroup) {
		m_subheadings?.forEach((sh) => {
			const extendedPath = ExtendedPath.create(sourceClass, sh.getClassPath(), defaultPathHints);

			// extendedPath won't always be available, for example in the case where we have multiple relationships, and 
			// one of the headings only applies to one of the relationships
			sh.resolveHeading(locale.localeResolver, null, extendedPath?.endClass, defaultSourceClass);

			if (needsDefaultImageIndex(sh, sh.getAttrType())) {
				sh.m_index = DEFAULT_IMAGE_LABEL;
			}

			sh.sourceKey = sh.untypedKey.copy({ extendedPath });
		});
	}
}

class SpecBuilder {
	/** register a custom naming function */
	static registerNameFunction(condition: SpecNameConditionFunction, value: SpecNameFunction): void {
		customNameFunctions.push([condition, value]);
	}

	/** additional preference paths from source class */
	readonly additionalPreferencePaths?: PreferencePath[];

	/** cache of specs by key (currently only holds specs that are asked to be cached, not all specs created by builder) */
	readonly cachedSpecs = new Map<C8AttributeKey, Spec>();

	/** context for spec creation (only necessary to support filtering/querySpec) */
	readonly context?: Context;

	/** path hints to use when resolving Spec when not provided */
	readonly defaultPathHints?: PathHints;

	/** class to use as start of extended path for Spec when not provided */
	readonly defaultSourceClass?: ReflectionClass;

	/** list of configured Headings in view */
	readonly headings: Heading[];

	/** path to source class in view/level (i.e. Relationship Id) */
	readonly pathToSource?: ExtendedPath;

	constructor(
		sourceClass?: C8NodeType | ReflectionClass,
		ctx?: Context,
		pathHints?: PathHints,
		pathToSource?: ExtendedPath,
		preferencePaths?: PreferencePath[],
		headings?: Heading[],
	) {
		makeObservable(this);
		this.additionalPreferencePaths = preferencePaths;
		this.context = ctx;
		this.defaultSourceClass = getClass(sourceClass);
		this.defaultPathHints = pathHints;
		this.headings = headings ?? [];
		this.pathToSource = pathToSource;
	}

	/** Headings configured explicitly in view mapped by Heading key */
	@computed({ keepAlive: true })
	get configuredHeadingMapByKey(): Map<string, Heading> {
		const { headings } = this;
		const map = new Map<string, Heading>();

		const mapAlternateHeadings = (h: Heading): void => {
			const key = h.getKey(true);

			if (!map.has(key)) {
				map.set(key, h);
			}
		};

		const mapHeading = (h: Heading): void => {
			map.set(h.getKey(), h);
		};

		// add exact keys to map for each Heading
		for (const h of headings) {
			mapHeading(h);
			h.configuredSubHeadings?.forEach(mapHeading);
		}

		// add alternate keys to map for each Heading
		for (const h of headings) {
			mapAlternateHeadings(h);
			h.configuredSubHeadings?.forEach(mapAlternateHeadings);
		}

		return map;
	}

	/** map of configured column matrix parameters needed to configure filters */
	@computed({ keepAlive: true })
	get configuredMatrixParamsMap(): Record<C8AttributeKey, IConfigurableQueryLevel> {
		const { context, configuredSpecs } = this;
		const map: Record<C8AttributeKey, IConfigurableQueryLevel> = {};

		for (const spec of configuredSpecs) {
			const { heading, matrixFilterKey, name } = spec;
			const configurableStore = heading.getMatrixProcessor()?.getColumnQueryLevelStore(context, heading);
			if (configurableStore) {
				map[matrixFilterKey] = {
					configurableStore,
					id: matrixFilterKey,
					name,
					type: configurableStore.typeNode,
				};
			}
		}

		return map;
	}

	/** map of configured column matrices from its target type to matrix key */
	@computed({ keepAlive: true })
	get configuredMatrixTypeToKeyMap(): Record<C8NodeType, C8AttributeKey> {
		const map: Record<C8NodeType, C8AttributeKey> = {};

		each(this.configuredMatrixParamsMap, (value) => {
			// it's possible there could be duplicates, but this unlikely with column matrices, and this
			// is now only used to load legacy data (so close enough)
			map[value.type] = value.id;
		});

		return map;
	}

	/** Specs configured explicitly in view mapped by ExtendedPath value, leaving out any matrix groups since they are grouped separately */
	@computed({ keepAlive: true })
	get configuredSpecMapByPath(): Record<string, Spec[]> {
		const { configuredMatrixGroupSpecs, configuredNonMatrixGroupSpecs } = this;
		const map = groupBy(configuredNonMatrixGroupSpecs, (spec) => pathPlusMatrixGroupId(spec.key.extendedPath));

		configuredMatrixGroupSpecs.forEach((matrixGroupSpec) => {
			const { matrixExtendedPath, matrixGroupId, subs } = matrixGroupSpec;
			const id = pathPlusMatrixGroupId(matrixExtendedPath, matrixGroupId);
			map[id] = subs;
		});

		return map;
	}

	/** list of configured specs in view that represent matrix groups */
	get configuredMatrixGroupSpecs(): Spec[] {
		return this.configuredSpecs.filter((spec) => spec.isMatrixGroup);
	}

	/** Specs configured explicitly in view mapped by ExtendedPath value */
	get configuredNonMatrixGroupSpecs(): Spec[] {
		return this.configuredSpecs.filter((spec) => !spec.isMatrixGroup);
	}

	/** set of paths used in view headings */
	get configuredPaths(): Set<string> {
		const paths = new Set<string>();

		this.headings.forEach(({ matrix, m_path }) => {
			if (m_path && (!matrix || matrix.flat)) {
				paths.add(m_path);
			}
		});

		return paths;
	}

	/** list of configured Specs in view */
	@computed({ keepAlive: true })
	get configuredSpecs(): Spec[] {
		// generate sub-specs for matrix groups with no path - in such case, the matrix is showing only
		// specific information about current class; column filtering is not allowed in such cases (no QuerySpec)
		// NOTE: this matrix spec does not take the place of specs generated by the matrix processor;
		// the purpose here is to have enough information to satisfy building the custom view. 
		// The specs used for rendering a matrix in a view could have a spec per column node, whereas the custom view only needs one
		const { defaultSourceType, getSpecForHeading, headings } = this;

		// configured headings don't apply to table dividers
		if (isDividerType(defaultSourceType)) {
			return [];
		}

		const specs = headings.map((h) => getSpecForHeading(h, h.isMatrixGroup ? h.configuredSubHeadings : null));
		return specs.filter((spec) => spec?.isAvailable);
	}

	/** Specs configured explicitly in view mapped by typed key */
	@computed({ keepAlive: true })
	get configuredSpecMap(): Map<C8AttributeKey, Spec> {
		const { configuredSpecs } = this;
		const map = new Map<C8AttributeKey, Spec>();

		const add = (spec: Spec): void => {
			const { uniqueId } = spec;

			if (!map.get(uniqueId)) {
				map.set(uniqueId, spec);
			}
		};

		for (const spec of configuredSpecs) {
			add(spec);
			spec.subs?.forEach(add);
		}

		return map;
	}

	/** default extended path */
	@computed({ keepAlive: true })
	get defaultSourcePath(): ExtendedPath {
		return ExtendedPath.create(this.defaultSourceClass, null, this.defaultPathHints);
	}

	/** default source type */
	get defaultSourceType(): C8NodeType | undefined {
		return this.defaultSourceClass?.type;
	}

	/** id for path hints; if path hints exist, then use their name, otherwise default to the source type; if neither is specified, then return a unique string as the answer is unknown */
	@computed({ keepAlive: true })
	get pathHintsId(): string {
		const { defaultPathHints, defaultSourceType } = this;
		const knownId = defaultPathHints ? defaultPathHints.name : defaultSourceType;
		return knownId || uniqueId("pathHintsId");
	}

	/** extended path starting from root class of view */
	@computed({ keepAlive: true })
	get rootPath(): ExtendedPath | undefined {
		const rc = this.context?.defView.getRootClass();
		return rc && ExtendedPath.create(rc);
	}

	/**
	 * Return Specs for any Headings in the view that are custom to view
	 */
	getConfiguredSpecsForPath = (extendedPath: ExtendedPath, matrixGroupId: string, includeEmptyPaths: boolean, usedHeadingKeys?: Set<string>): Spec[] => {
		const { configuredSpecMapByPath } = this;
		const id = pathPlusMatrixGroupId(extendedPath, matrixGroupId);
		let specs = configuredSpecMapByPath[id] ?? [];

		if (includeEmptyPaths) {
			specs = specs.concat(configuredSpecMapByPath[DEFAULT_PATH] ?? []);
		}

		if (usedHeadingKeys) {
			return specs.filter((spec) => !usedHeadingKeys.has(spec.headingKey));
		}

		return specs;
	};

	/**
	 * Create and return a Spec for the given key; also keep track of the spec as it will likely be asked for again
	 */
	getCachedSpec = (key: C8AttributeKey | Key, createIfNotFound?: boolean, ignoreMatrixGroup?: boolean, params?: Partial<Spec>): Spec | undefined => {
		const { cachedSpecs, configuredSpecMap, getKeyForSourceClass, getSpecForKey } = this;
		let keyObj = Key.toKey(key);
		let spec: Spec;

		if (!keyObj.extendedPath) {
			keyObj = getKeyForSourceClass(key);
		}

		if (keyObj) {
			const { typedString } = keyObj;

			spec = configuredSpecMap[typedString] || cachedSpecs[typedString];

			// ignore spec that is a matrix group
			if (ignoreMatrixGroup && spec?.isMatrixGroup) {
				spec = null;
			}

			if (!spec && createIfNotFound) {
				spec = cachedSpecs[typedString] = getSpecForKey(key, params, null, null, ignoreMatrixGroup);
			}
		}

		return spec;
	};

	/**
	 * Return key for configured heading that uses the Name property for the given path and matrix, if found
	 */
	getConfiguredNamePropertyForPath = (extendedPath: ExtendedPath, matrixGroupId?: string, usedKeys?: Set<string>): Key | undefined => {
		const untypedKey = def.getHeadingKey(extendedPath.standardPath, null, xml.PROP_NAME, null, null, matrixGroupId);
		const configuredHeading = this.configuredHeadingMapByKey.get(untypedKey);
		if (configuredHeading) {
			usedKeys?.add(configuredHeading.getKey());

			return new Key({
				extendedPath,
				matrix: matrixGroupId,
				name: configuredHeading.configuredName,
				prop: xml.PROP_NAME,
			});
		}
	};

	/**
	 * Return Key for given heading
	 * @param matrixPath - if provided, this acts as the starting point for heading paths
	 * @param validate - if true, then return undefined if attribute is not in model along the given path
	 */
	getKeyForHeading = (heading: Heading, matrixPath?: ExtendedPath, validate?: boolean): Key | undefined => {
		const {
			configuredName: name,
			parentMatrixGroupId: matrix,
			sourceKey,
			m_id: id,
			m_index: index,
			m_path: path,
			m_prop: prop,
		} = heading;

		if (sourceKey) {
			// NOTE: only concern here is the index, but at this time I don't think it is a problem because
			// column matrices where the same heading is used for multiple indices do not use this code
			return sourceKey;
		}

		const sourcePath = matrixPath || this.defaultSourcePath;
		let extendedPath: ExtendedPath;

		try {
			extendedPath = sourcePath.plus(path);

			if (id) {
				extendedPath = getConcretePathForAttribute(id, extendedPath, validate);
				if (validate && !extendedPath) {
					return;
				}
			}
		} catch (ex) {
			// return nothing if validating and path is not valid
			if (validate) {
				return;
			}
		}

		const key = new Key({
			extendedPath,
			id,
			index,
			matrix,
			name,
			path,
			prop,
		});

		heading.sourceKey = key;
		heading.valueType = key.valueType;

		return key;
	};

	/**
	 * Return new key that is similar to this key but with a different start class;
	 * if the key does not make sense (attribute does not exist for the new start class),
	 * then return undefined
	 */
	getKeyForSourceClass = (key: Key | C8AttributeKey): Key | undefined => {
		key = Key.toKey(key);
		const { defaultSourcePath } = this;
		const { id, path } = key;

		// in rare case the source path is not even typed (such as when Target="Node") - 
		// in such case just return original key and assume it is valid
		if (!defaultSourcePath.startClass) {
			return key;
		}

		try {
			const extendedPath = defaultSourcePath.plus(path);

			if (!id || (id === ATTR_TYPE) || getClassAttribute(extendedPath.endClass, id)) {
				return key.copy({ extendedPath });
			}
		} catch (ex) {
			// return undefined
		}
	};

	/**
	 * Return matrix group path for heading
	 */
	getMatrixGroupExtendedPath = (heading: Heading): ExtendedPath | undefined => {
		const { defaultPathHints, defaultSourceClass, rootPath } = this;
		const { isMatrixGroup, matrixGroupPath } = heading;

		if (isMatrixGroup) {
			const mp = heading.getMatrixProcessor();
			return mp.bRelativeToRoot ? rootPath : ExtendedPath.create(defaultSourceClass, matrixGroupPath, defaultPathHints);
		}
	};

	/**
	 * Return the matrix group key for the given id
	 */
	getMatrixGroupSpecForId = (matrixGroupId: string): Spec => {
		return this.configuredMatrixGroupSpecs.find((spec) => spec.matrixGroupId === matrixGroupId);
	};

	/**
	 * Create spec for matrix group with given keys - this only creates basically a placeholder
	 * for the matrix group structure rather than the matrix group itself (potentially repeated for multiple columns)
	 */
	getMatrixGroupSpecForKey = (matrixGroupKey: Key | C8AttributeKey, groupedKeys: C8AttributeKey[]): Spec | undefined => {
		const { getMatrixGroupExtendedPath, getSpecForHeading } = this;
		const key = Key.toKey(matrixGroupKey);
		const heading = this.#getHeading(key);
		const matrixExtendedPath = getMatrixGroupExtendedPath(heading);

		// it's possible that a matrix no longer exists in the view for what is thought to be a matrix key, so 
		// return undefined if not found
		if (heading.isMatrixGroup) {
			heading.clearSubheadings();

			groupedKeys.forEach((groupedKey) => {
				const subkey = Key.toKey(groupedKey);
				const subheading = this.#getHeading(subkey, matrixExtendedPath);
				heading.addSubHeading(subheading, true);
			});

			return getSpecForHeading(heading, heading.m_subheadings);
		}
	};

	/**
	 * Return Spec for given heading, and for a matrix group heading optionally its sub-headings
	 */
	getSpecForHeading = (heading: Heading, matrixGroupHeadings?: Heading[]): Spec | undefined => {
		const { getKeyForHeading } = this;
		const key = getKeyForHeading(heading);
		let subs: Spec[];

		if (heading.isMatrixGroup) {
			const matrixPath = this.getMatrixGroupExtendedPath(heading);
			if (!matrixPath) {
				return null;
			}

			subs = matrixGroupHeadings?.map((sh) => this.#createHeadingSpec({
				heading: sh,
				key: getKeyForHeading(sh, matrixPath),
			})) ?? [];
		}

		return this.#createHeadingSpec({
			heading,
			key,
			subs,
		});
	};

	/**
	 * Return Spec for given key, such as those stored in a custom view
	 * @param key - key used to identify property or attribute
	 * @param params - optional parameters to include in the Spec
	 * @param usedSpecs - if provided and configured Heading is used, then it will be added to the list
	 * @param matrixExtendedPath - provide if spec will be a sub-spec of a matrix
	 * @param ignoreMatrixGroup - if true, then ignore matrix group specs (could be simple list instead)
	 */
	getSpecForKey = (key: Key | C8AttributeKey, params?: Partial<Spec>, usedKeys?: Set<string>, matrixExtendedPath?: ExtendedPath, ignoreMatrixGroup?: boolean): Spec | undefined => {
		key = Key.toKey(key);

		// if key is untyped, then convert to a typed key
		if (!key.extendedPath) {
			key = this.getKeyForSourceClass(key);
		}

		let heading = params?.heading;

		if (!heading) {
			// the key can possibly change if of an older format, so use source key for heading that was found
			heading = this.#getHeading(key, matrixExtendedPath, usedKeys, ignoreMatrixGroup);
			key = heading.sourceKey;
		}

		return this.#createHeadingSpec({
			...params,
			heading,
			index: key.index,
			key,
		});
	};

	/**
	 * Return true if given builder uses same model (start class and path hints)
	 */
	hasSameModelAs(specBuilder: SpecBuilder): boolean {
		const { defaultSourceClass, defaultPathHints } = this;

		if (specBuilder === this) {
			return true;
		}

		return (defaultSourceClass.type === specBuilder.defaultSourceClass.type) &&
			(defaultPathHints?.name === specBuilder.defaultPathHints?.name);
	}

	/**
	 * Return Spec for given key, which can either be a string or Key object
	 */
	#createHeadingSpec(params: Partial<Spec>): Spec | undefined {
		// resolve key against model to ensure that key is valid
		resolveSpecParameters(this, params);

		// augment name for image labels and attributes along a path with a name
		const { heading, key } = params;
		const { extendedPath } = key;
		
		if (heading && extendedPath) {
			const { isMatrixGroup, isName, m_name } = heading;

			// assign custom name for keys that meet certain conditions
			customNameFunctions.some(([condition, value]) => {
				if (condition(key)) {
					heading.m_name = value(key);
					return true;
				}
			});

			if (!isMatrixGroup) {
				// apply path name if not done so already, and path name does not already match the given name (i.e. don't want Sample Sample)
				const pn = configPreferences.getPathName(extendedPath);
				if (pn && (pn !== m_name)) {
					if (isName && pn.endsWith(m_name)) {
						heading.m_label = pn;
					} else {
						heading.m_label = locale.resource("ConjunctionHeadingWithPath", [m_name, pn]);
					}
				}
			}
		}

		const spec = new Spec(params);

		if (heading?.canQuery()) {
			const ctx = this.context?.view?.getContext() || state.get("userContext");
			const qs = csiHeading.getQuerySpec(ctx, spec);
			spec.setQuerySpec(qs);
		}

		return spec;
	}

	/**
	 * Return heading for key that is either pre-configured or created dynamically
	 */
	#getHeading(key: Key, matrixExtendedPath?: ExtendedPath, usedKeys?: Set<string>, ignoreMatrixGroup?: boolean): Heading {
		const { configuredHeadingMapByKey, getKeyForHeading } = this;
		const headingKey = matrixExtendedPath ? key.getKeyRelativeToPath(matrixExtendedPath) : key;
		const {
			extendedPath,
			id,
			index,
			keyRel,
			matrix,
			name,
			path,
			prop,
			reflectionAttribute,
		} = headingKey;

		let heading: Heading;

		// lookup Heading from list of configured ones
		const untypedKey = def.getHeadingKey(key.path, id, prop, name, index, matrix);
		const configuredHeading = configuredHeadingMapByKey.get(untypedKey);
		if (configuredHeading) {
			// validate that the configured heading is correct for this key
			const { sourceKey } = configuredHeading;

			if (!sourceKey || !reflectionAttribute || (reflectionAttribute === sourceKey.reflectionAttribute)) {
				heading = configuredHeading;

				// track used keys - use heading's normal key as opposed to untypedKey calculated here. There can be alternate keys
				// for the same Heading (i.e. getKey(true)), so need to use standard one for lookup to match in getConfiguredSpecsForPath
				usedKeys?.add(heading.getKey());
			}
		}

		// ignore heading if for query is true and the heading is a matrix group
		if (ignoreMatrixGroup && configuredHeading?.isMatrixGroup) {
			heading = null;
		}

		// create Heading if not given or not pre-configured
		if (!heading) {
			heading = new Heading();

			// set up the rca as well if extendedPath specified
			const targetClass = extendedPath?.endClass;
			if (targetClass) {
				heading._rca = getClassAttribute(targetClass, id);
			}

			heading.m_id = id;
			heading.m_prop = prop;
			heading.m_index = index;
			heading.m_name = name;
			heading.m_path = path;
		}

		// if key "level" is -1, then update key to new syntax for matrices; -1 was previously used to indicate the client did not understand the model for the heading
		if (keyRel === -1) {
			key = getKeyForHeading(heading);
		}

		// save key where heading first came from
		heading.sourceKey ||= key;
		heading.valueType ||= key.valueType;

		return heading;
	}
};

// register custom name function for image labels
SpecBuilder.registerNameFunction(
	(key) => isImageLabelKey(key) && !!key.extendedPath.endClass.imageLabel,
	({ extendedPath, index }) => locale.getEnumName(xml.enumCreate(extendedPath.endClass.imageLabel, index.toString())),
);

SpecBuilder.registerNameFunction(
	(key) => key.isForShowingReferencedImages,
	({ extendedPath, id }) => locale.resource("PreferenceAttributesImages", [locale.typeId(extendedPath.endType, id)]),
);

export = SpecBuilder;
